/*
  Bra size calculator — <bra-size-calculator>

  Deterministic lookup. Same inputs always give the same size.

  Source of truth: the size chart rendered in this drawer (cm + inches tables from the
  section's size_guide_data), parsed at runtime. A drawer carries only ONE chart — band/cup
  for women_bras, letter sizes for bras_regular/bras_plus — so the missing system falls back
  to assets/bra-size-rules.json.

  Rules (D-015, mockups 2026-09-04): the underband measurement decides the letter size;
  between sizes never moves the recommendation. Only the system this product sells in is
  shown ("Your size in this bra"). Review feedback is one attributed line and never changes
  the size. Fields are empty until the customer types (except values they entered before).
*/
(function () {
  if (customElements.get('bra-size-calculator')) return;

  const IN_TO_CM = 2.54;
  const FIT_MIN_REVIEWS = 20, FIT_SMALL = 1.65, FIT_LARGE = 2.35;

  // "80-82,5" | "76 ½ - 80 ½" | "30 - 31 ¾" | "76.2-78.7"  ->  [lo, hi]
  function parseRange(text) {
    let s = String(text)
      .replace(/\s*½/g, '.5').replace(/\s*¼/g, '.25').replace(/\s*¾/g, '.75')
      .replace(/(\d),(\d)/g, '$1.$2');
    const nums = (s.match(/\d+(?:\.\d+)?/g) || []).map(Number);
    if (!nums.length) return null;
    return [nums[0], nums[nums.length - 1]];
  }

  function rowsOf(table) {
    return [...table.querySelectorAll('tr')].map(tr => ({
      cells: [...tr.children].map(td => td.textContent.replace(/\s+/g, ' ').trim()),
      hasColspan: !!tr.querySelector('[colspan]'),
    }));
  }

  // Band/cup grid: header row 1 = bust ranges, body rows = underband range + sizes.
  function parseGrid(cmTable, inTable) {
    const cm = rowsOf(cmTable);
    if (!cm.length || !cm[0].hasColspan) return null;
    const header = cm[1].cells.slice(1).map(parseRange);
    const body = cm.slice(2).filter(r => r.cells.length > 1);
    const out = {
      source: 'chart',
      bust_cm: header,
      underband_cm: body.map(r => parseRange(r.cells[0])),
      eu: body.map(r => r.cells.slice(1)),
      us: null,
    };
    if (inTable) {
      const inch = rowsOf(inTable).slice(2).filter(r => r.cells.length > 1);
      out.us = inch.map(r => r.cells.slice(1));
    }
    if (out.bust_cm.some(x => !x) || out.underband_cm.some(x => !x)) return null;
    return out;
  }

  // Alpha table: header = sizes, rows labelled Bust / Underband / Cup (EU|US|FR).
  function parseAlpha(cmTable) {
    const rows = rowsOf(cmTable);
    if (!rows.length || rows[0].hasColspan) return null;
    const sizes = rows[0].cells.slice(1).map(s => s.toUpperCase());
    const out = { source: 'chart', sizes, bust_cm: null, underband_cm: null, cups_eu: null };
    rows.slice(1).forEach(r => {
      const label = r.cells[0].toLowerCase();
      const vals = r.cells.slice(1);
      if (label.startsWith('bust')) out.bust_cm = vals.map(parseRange);
      else if (label.startsWith('underband')) out.underband_cm = vals.map(parseRange);
      else if (label.startsWith('cup (eu)')) out.cups_eu = vals;
    });
    if (!out.underband_cm || out.underband_cm.some(x => !x)) return null;
    return out;
  }

  // Index of the range containing v; else nearest range within tolerance; else -1.
  function findRange(ranges, v, tol) {
    for (let i = 0; i < ranges.length; i++) {
      if (v >= ranges[i][0] && v <= ranges[i][1]) return i;
    }
    let best = -1, bestDist = Infinity;
    for (let i = 0; i < ranges.length; i++) {
      const d = v < ranges[i][0] ? ranges[i][0] - v : v - ranges[i][1];
      if (d < bestDist) { bestDist = d; best = i; }
    }
    return bestDist <= tol ? best : -1;
  }

  function resolveAlpha(a, ubCm, bustCm, tol) {
    const r = { size: null, note: null, cups: null, source: a.source };
    const ub = findRange(a.underband_cm, ubCm, tol);
    const bu = a.bust_cm ? findRange(a.bust_cm, bustCm, tol) : -1;
    let idx = -1;
    if (ub >= 0) {
      // The underband measurement decides the size (D-015). If the bust lands in a
      // different size, say "between sizes" but do not change the recommendation.
      idx = ub;
      if (bu >= 0 && bu !== ub) r.note = 'between';
    } else if (bu >= 0) {
      idx = bu; r.note = 'bust_only';
    }
    if (idx >= 0) {
      r.size = a.sizes[idx];
      if (a.cups_eu && a.cups_eu[idx]) r.cups = a.cups_eu[idx];
    }
    return r;
  }

  // Between-sizes rule (Alexander, 2026-09-04): the underband decides the row. If the bust
  // falls in a gap or on an empty cell, snap to the NEAREST FILLED column on that row (up to
  // BUST_SNAP_CM away) and flag it, so the note explains the choice. Beyond that: no-carry.
  // The snap only engages when the bust is NOT inside a filled cell's own range.
  // 1.5 cm cap per Ida (2026-09-04): column steps are ~a full grading, so a bigger jump
  // risks recommending a size too small/large; underband rows stay elastic-tolerant.
  const BUST_SNAP_CM = 1.5;

  function resolveBandcup(b, ubCm, bustCm, tol) {
    const r = { eu: null, us: null, reason: null, between: false, source: b.source };
    const inside = (ranges, v) => ranges.some(x => v >= x[0] && v <= x[1]);
    const ub = findRange(b.underband_cm, ubCm, tol);
    if (ub < 0) { r.reason = 'underband_out'; return r; }
    if (!inside(b.underband_cm, ubCm)) r.between = true;
    const row = b.eu[ub] || [];
    let bu = findRange(b.bust_cm, bustCm, tol);
    if (bu < 0) { r.reason = 'bust_out'; return r; }
    if (!(row[bu] || '').trim()) {
      // Her bust lands on an empty cell — one of the chart's two corners, small band with a
      // large bust or the reverse. The fit team's rule (Ida, 2026-09-18):
      //   "Vi hopper kun en størrelse - enten op eller ned ud fra skemaet - ikke til siderne"
      //   "underband skal lead the way - så vi skal så tæt på at det matcher som muligt
      //    - så må cup følge med"
      // The underband ROW is fixed and the cup may move ONE step, no further. Not sister
      // sizing: we do not change the band to rescue the cup, even though 70C and 75B are
      // the same cup volume, because the band is the part that has to fit.
      //
      // Measured in CUP STEPS, not centimetres. The old test was bestDist > 1.5cm, a
      // different question — the columns are 2.5-4cm wide, so a centimetre threshold
      // allowed or refused the same hop depending where in her column she fell.
      let best = -1;
      if ((row[bu - 1] || '').trim()) best = bu - 1;
      else if ((row[bu + 1] || '').trim()) best = bu + 1;
      if (best < 0) { r.reason = 'combo_out'; return r; }
      bu = best;
      r.approx = true;   // the nearest we make, and she is told so — not "between sizes"
    } else if (!inside(b.bust_cm, bustCm)) {
      r.between = true;
    }
    r.eu = row[bu].trim();
    r.us = b.us && b.us[ub] && b.us[ub][bu] ? b.us[ub][bu].trim() : null;
    return r;
  }

  // Yotpo scores 1–3, 2 = true to size; 1 = runs small (direction verified 2026-09-01).
  function fitFromMetafield(json) {
    try {
      const parsed = JSON.parse(json) || {};
      const bl = parsed.bottomline || (parsed.totalReview != null || parsed.total_review != null ? parsed : {});
      const out = { reviews: Number(bl.totalReview || bl.total_review || 0), fit: null, underband: null, cup: null, product_fit: null };
      const cf = bl.customFieldsBottomline || bl.custom_fields_bottomline || {};
      Object.values(cf).forEach(v => {
        const t = String(v.title || '').toLowerCase(), sc = v.score != null ? Number(v.score) : null;
        if (t === 'fit') out.fit = sc; else if (t === 'underband') out.underband = sc;
        else if (t === 'cup') out.cup = sc; else if (t === 'product fit') out.product_fit = sc;
      });
      return out.reviews ? out : null;
    } catch (e) { return null; }
  }

  function fitVerdict(score) {
    if (score == null) return null;
    return score <= FIT_SMALL ? 'small' : score >= FIT_LARGE ? 'large' : 'true';
  }

  // The unit control is a custom listbox, not a <select>. A native select's OPEN menu is
  // drawn by the browser and ignores CSS entirely, so the closed field matched the design
  // and the menu did not. Ported from the style guide's bindSelect (fit-quiz.js) rather
  // than written again — Alexander, 2026-09-18, "reuse the dropdown work you did for the
  // style guide. We just want to have same styling."
  //
  // Behaviour comes with it: arrows wrap, Enter and Space choose, Escape closes and returns
  // focus to the button, an outside click closes, options are tabIndex -1 with focus
  // managed, and choosing dispatches 'change' on the hidden input so every existing reader
  // of this.unit.value keeps working.
  //
  // ONE ADDITION over the quiz's version. setUnit() can write this.unit.value directly —
  // from the drawer's cm/inches chips, or when a measurement is re-applied — and the quiz's
  // bindSelect only repaints the button inside choose(). That would leave the button
  // reading "Centimeters" while the value said "in". The input's own change event is the
  // one signal both paths share, so the paint hangs off that instead.
  function bindUnitSelect(root) {
    if (!root) return;
    const btn = root.querySelector('[data-unitbtn]');
    const list = root.querySelector('[data-unitlist]');
    const label = root.querySelector('[data-unitlabel]');
    const input = root.querySelector('input[type="hidden"]');
    if (!btn || !list || !label || !input) return;
    const opts = [...list.querySelectorAll('[role="option"]')];
    if (!opts.length) return;

    const paint = () => {
      const cur = opts.find(o => o.dataset.val === input.value) || opts[0];
      label.textContent = cur.textContent.trim();
      opts.forEach(x => x.setAttribute('aria-selected', String(x === cur)));
    };
    paint();
    input.addEventListener('change', paint);   // covers programmatic changes too

    const open = (on) => {
      list.hidden = !on;
      btn.setAttribute('aria-expanded', String(on));
      root.classList.toggle('is-open', on);
      if (on) (opts.find(o => o.dataset.val === input.value) || opts[0]).focus();
    };
    const choose = (o) => {
      input.value = o.dataset.val;
      open(false);
      btn.focus();
      input.dispatchEvent(new Event('change', { bubbles: true }));  // paints, and tells the calculator
    };
    btn.addEventListener('click', () => open(list.hidden));
    list.addEventListener('click', (e) => {
      const o = e.target.closest('[role="option"]');
      if (o) choose(o);
    });
    list.addEventListener('keydown', (e) => {
      const i = opts.indexOf(document.activeElement);
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        opts[(i + (e.key === 'ArrowDown' ? 1 : opts.length - 1)) % opts.length].focus();
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault(); if (i > -1) choose(opts[i]);
      } else if (e.key === 'Escape') { open(false); btn.focus(); }
    });
    document.addEventListener('click', (e) => {
      if (!list.hidden && !root.contains(e.target)) open(false);
    });
    opts.forEach(o => { o.tabIndex = -1; });
  }

  class BraSizeCalculator extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.unit = this.querySelector('[name="unit"]');
      this.ub = this.querySelector('[name="underband"]');
      this.bust = this.querySelector('[name="bust"]');
      this.result = this.querySelector('[data-result]');
      this.submit = this.querySelector('[data-submit]');
      this.shop = this.querySelector('[data-shop]');
      this.rulesUrl = this.dataset.rulesUrl;
      this.sizing = this.dataset.sizing || 'alpha'; // the system this product sells in
      // Belt and braces: a Cup picker on the PDP means this product is band/cup sized.
      if (document.querySelector('.product-form__input input[type="radio"][name="Cup"]')) this.sizing = 'bandcup';
      this.copy = JSON.parse(this.querySelector('[data-copy]').textContent);
      this.tol = 1.5;
      this.chart = null;
      this.fallback = null;
      this.fit = undefined;
      this.currentUnit = this.unit.value;

      // The theme's modal-popup preventDefault()s every non-link click on <body> while a
      // drawer is open, which swallows form submission. Act on element-level events.
      this.unit.addEventListener('change', () => this.setUnit(this.unit.value, true));
      bindUnitSelect(this.querySelector('[data-unitselect]'));
      const guide = this.closest('size-guide');
      if (guide) {
        guide.querySelectorAll('.chip-link.centimeters-tab, .chip-link.inches-tab').forEach(tab => {
          tab.addEventListener('click', () => this.setUnit(tab.classList.contains('inches-tab') ? 'in' : 'cm', false));
        });
      }
      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.calculate(); });
      this.submit.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); this.calculate(); });
      [this.ub, this.bust].forEach(el => {
        el.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); this.calculate(); } });
        el.addEventListener('input', () => this.scheduleLive());
      });
      this.onUnitChange();

      // Do nothing heavy until the drawer is opened: chart parsing and the JSON fetches
      // only happen for customers who use it.
      const modal = this.closest('modal-popup');
      const start = () => { if (this.started) return; this.started = true; this.parseChart(); this.prefill(); };
      if (modal) modal.addEventListener('modal-open', start);
      else start();
      [this.ub, this.bust].forEach(el => el.addEventListener('focus', start, { once: true }));
    }

    // No auto-fill (design 2026-09-04): the fields keep their placeholders until the
    // customer types. Nothing is restored from storage.
    prefill() {}

    // Switch units: convert typed values, update steps, sync the chart tab, recalc.
    setUnit(u, syncChart) {
      const prev = this.currentUnit;
      if (this.unit.value !== u) this.unit.value = u;
      if (prev !== u) {
        const conv = (v) => {
          const n = parseFloat(String(v).replace(',', '.'));
          if (!(n > 0)) return v;
          return u === 'in' ? String(Math.round((n / IN_TO_CM) * 4) / 4) : String(Math.round(n * IN_TO_CM * 2) / 2);
        };
        this.ub.value = conv(this.ub.value);
        this.bust.value = conv(this.bust.value);
        this.currentUnit = u;
      }
      this.onUnitChange();
      if (syncChart) {
        const guide = this.closest('size-guide');
        const tab = guide && guide.querySelector(u === 'in' ? '.chip-link.inches-tab' : '.chip-link.centimeters-tab');
        if (tab && !tab.classList.contains('active')) tab.click();
      }
      this.calculate(true);
    }

    onUnitChange() {
      const u = this.unit.value;
      const suffix = u === 'in' ? this.copy.unit_in : this.copy.unit_cm;
      this.ub.placeholder = this.copy.underband_placeholder.replace('%s', suffix);
      this.bust.placeholder = this.copy.bust_placeholder.replace('%s', suffix);
      this.ub.step = this.bust.step = u === 'in' ? '0.25' : '0.5';
    }

    // Parse the chart rendered in this drawer (synchronous, cheap).
    parseChart() {
      if (this.chart) return this.chart;
      const guide = this.closest('size-guide') || document;
      const cmTable = guide.querySelector('.centimeters-content table');
      const inTable = guide.querySelector('.inches-content table');
      const chart = { alpha: null, bandcup: null };
      if (cmTable) {
        chart.bandcup = parseGrid(cmTable, inTable);
        if (!chart.bandcup) chart.alpha = parseAlpha(cmTable);
      }
      this.chart = chart;
      return chart;
    }

    // Fetch the JSON only for the sizing system this product's chart lacks.
    prepare() {
      if (this.preparePromise) return this.preparePromise;
      const chart = this.parseChart();
      const needFallback = !chart.alpha || !chart.bandcup;
      this.preparePromise = needFallback
        ? fetch(this.rulesUrl).then(r => r.json()).then(j => { this.fallback = j; this.tol = j.gap_tolerance_cm || this.tol; })
            .catch(() => { this.fallback = null; })
        : Promise.resolve();
      return this.preparePromise;
    }

    // Live Yotpo metafield first; else the per-style snapshot asset.
    loadFit() {
      if (this.fitPromise) return this.fitPromise;
      const mf = this.querySelector('[data-yotpo]');
      const live = mf && mf.textContent.trim() ? fitFromMetafield(mf.textContent) : null;
      if (live) { this.fit = live; this.fitPromise = Promise.resolve(live); return this.fitPromise; }
      const url = this.dataset.fitUrl, style = this.dataset.style;
      this.fitPromise = (url && style)
        ? fetch(url).then(r => r.json()).then(j => { this.fit = (j.styles && j.styles[style]) || null; return this.fit; }).catch(() => (this.fit = null))
        : Promise.resolve(this.fit = null);
      return this.fitPromise;
    }

    // Live mode: recalc ~400ms after typing stops; no error nagging while typing.
    scheduleLive() {
      clearTimeout(this.liveTimer);
      this.liveTimer = setTimeout(() => this.calculate(true), 400);
    }

    async calculate(quiet) {
      this.started = true;
      const u = this.unit.value;
      let ub = parseFloat(String(this.ub.value).replace(',', '.'));
      let bu = parseFloat(String(this.bust.value).replace(',', '.'));
      if (!(ub > 0) || !(bu > 0)) {
        if (quiet) this.reset(); else this.render({ error: this.copy.error_missing });
        return;
      }
      if (u === 'in') { ub *= IN_TO_CM; bu *= IN_TO_CM; }
      if (bu <= ub) {
        if (quiet) this.reset(); else this.render({ error: this.copy.error_bust_smaller });
        return;
      }
      await this.prepare();
      await this.loadFit();
      this.quiet = !!quiet;
      if (!quiet) {
        try { localStorage.setItem(this.dataset.storageKey || 'ob-bra-size-calc', JSON.stringify({ unit: u, ub: this.ub.value, bust: this.bust.value })); } catch (e) {}
      }
      const fb = this.fallback || {};
      const alphaRules = (this.chart && this.chart.alpha) || (fb.alpha ? { ...fb.alpha, source: 'fallback' } : null);
      const bcRules = (this.chart && this.chart.bandcup) || (fb.bandcup ? { ...fb.bandcup, source: 'fallback' } : null);
      this.render({
        alpha: alphaRules ? resolveAlpha(alphaRules, ub, bu, this.tol) : null,
        bandcup: bcRules ? resolveBandcup(bcRules, ub, bu, this.tol) : null,
      });
    }

    // Clear any previous result and CTA (used when inputs become empty/invalid).
    reset() {
      this.result.hidden = true;
      this.result.innerHTML = '';
      this.shop.hidden = true;
      this.submit.hidden = false;
    }

    // Mockups 2026-09-04: ONE size — the system this product sells in — in a green panel;
    // grey no-carry panel otherwise; "Shop my size" replaces "Find my size" on a match.
    render(r) {
      const c = this.copy, el = this.result;
      el.hidden = false;
      this.shop.hidden = true;
      this.submit.hidden = false;
      if (r.error) { el.innerHTML = `<p class="bra-calc__error" role="alert">${r.error}</p>`; return; }
      this.submit.hidden = true; // a result is shown; live typing keeps it updated

      const a = r.alpha, b = r.bandcup;
      const primary = this.sizing === 'bandcup' ? (b && b.eu) : (a && a.size);
      const src = this.sizing === 'bandcup' ? b : a;
      const fromFallback = src && src.source === 'fallback';

      if (!primary) {
        el.innerHTML = `<div class="bra-calc__panel bra-calc__panel--none"><p>${c.no_carry}</p></div>`;
        return;
      }

      // Between sizes: the explanation lives INSIDE the panel, above a reworded label.
      const between = (this.sizing === 'alpha' && a && a.note === 'between') || (this.sizing === 'bandcup' && b && b.between);
      let note = '';
      if (this.sizing === 'alpha' && a && a.note === 'bust_only') note = c.note_bust_only;
      const canShop = !!this.findVariantInputs(primary);
      const hasPicker = !!document.querySelector('.product-form__input input[type="radio"][name="Size"]');

      el.innerHTML = `
        <div class="bra-calc__panel">
          ${between
            ? `<p class="bra-calc__panel-note">${c.note_between} ${c.result_prefix_between}</p>`
            : `<p class="bra-calc__panel-label">${c.result_prefix}</p>`}
          <p class="bra-calc__panel-size">${primary}${fromFallback ? ` <span class="bra-calc__src">${c.src_fallback}</span>` : ''}</p>
          ${this.renderFit()}
        </div>
        ${note ? `<p class="bra-calc__note">${note}</p>` : ''}
        ${!canShop && hasPicker ? `<p class="bra-calc__note">${c.not_in_style.replace('%s', primary)}</p>` : ''}`;

      if (canShop) {
        this.shop.hidden = false;
        this.shop.textContent = c.shop_size;
        this.shop.onclick = (e) => { e.preventDefault(); e.stopPropagation(); this.selectVariant(primary); };
      }
      if (!this.quiet) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    renderFit() {
      const f = this.fit, c = this.copy;
      if (!f || !(f.reviews >= FIT_MIN_REVIEWS)) return '';
      const band = fitVerdict(f.underband), cup = fitVerdict(f.cup);
      let overall = fitVerdict(f.fit != null ? f.fit : f.product_fit);
      if (!overall && (band || cup)) {
        overall = [band, cup].includes('small') ? 'small' : [band, cup].includes('large') ? 'large' : 'true';
      }
      if (!overall) return '';
      // One line; the most specific true flag wins (D-015).
      let line;
      if (band === 'small') line = c.fit_band_small;
      else if (cup === 'small') line = c.fit_cup_small;
      else if (band === 'large') line = c.fit_band_large;
      else if (cup === 'large') line = c.fit_cup_large;
      else line = overall === 'small' ? c.fit_small : overall === 'large' ? c.fit_large : c.fit_true;
      return `<p class="bra-calc__panel-fit"><span>${c.fit_label.replace('%s', Number(f.reviews).toLocaleString())}</span><br>${line}</p>`;
    }

    // Alpha: one radio named "Size". Band/cup: band radio "75" + cup radio "C" after the
    // theme's createBraOptions() split; before the split they carry the full "75C" value.
    findVariantInputs(size) {
      const q = (name, val) =>
        document.querySelector(`.product-form__input input[type="radio"][name="${name}"][value="${CSS.escape(val)}"]`);
      const single = q('Size', size);
      if (single) return [single];
      const m = size.match(/^(\d+)([A-Z/]+)$/);
      if (m) {
        const band = q('Size', m[1]);
        const cup = q('Cup', m[2]) || q('Cup', m[2].split('/')[0]);
        if (band && cup) return [band, cup];
      }
      return null;
    }

    // Close the drawer FIRST (modal-popup preventDefault()s clicks while open, and a
    // cancelled click on a radio reverts the check), then set checked + dispatch change.
    // Desktop comps 2026-09-04: the CTA reads "Add my size to cart" — after selecting the
    // variant we press the PDP's own add-to-cart button once variant-radios has settled;
    // if it's missing or disabled (sold out), the size is still selected and visible.
    selectVariant(size) {
      const inputs = this.findVariantInputs(size);
      if (!inputs) return;
      const modal = this.closest('modal-popup');
      if (modal) {
        if (typeof modal.modalClose === 'function') modal.modalClose();
        else modal.querySelector('.modal__close-button')?.click();
      }
      inputs.forEach(i => {
        i.checked = true;
        i.dispatchEvent(new Event('change', { bubbles: true }));
      });
      const picker = inputs[0].closest('variant-radios, variant-selects') || inputs[0].closest('fieldset');
      if (picker && picker.scrollIntoView) picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        const atc = document.querySelector('form[action*="/cart/add"] [name="add"]:not([disabled]), product-form [name="add"]:not([disabled])');
        if (atc) atc.click();
      }, 450);
    }
  }

  customElements.define('bra-size-calculator', BraSizeCalculator);
})();
